import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./clients.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";

export default function Clients() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, isAdmin, language } = auth;

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  const handleClientSelect = useCallback((client) => {
    setSelectedClient(client);
    setDraft({ ...client });
    setIsEditing(false);
  }, []);

  async function saveClient() {
    if (!draft?.client_id) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/admin/clients`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: draft.client_id,
          owner_name: draft.owner_name || "",
          phone: draft.phone || "",
          email: draft.email || "",
          city: draft.city || "",
          business_name: draft.business_name || "",
          tier_plan: draft.tier_plan || "none",
          tier_billing: (draft.tier_billing || "monthly").toLowerCase(),
          contact_ok: !!draft.contact_ok,
          client_request: draft.client_request || "",
          next_action: draft.next_action || "",
          comment_feedback: draft.comment_feedback || "",
          waba_phone_number_id: draft.waba_phone_number_id || "",
          d360_api_key: draft.d360_api_key || "",
          active_ends_at: draft.active_ends_at || null,

          admin_numbers: typeof draft.admin_numbers === "string"
            ? draft.admin_numbers.split(",").map(s => s.trim()).filter(Boolean)
            : (Array.isArray(draft.admin_numbers) ? draft.admin_numbers : null),

          oper_numbers: typeof draft.oper_numbers === "string"
            ? draft.oper_numbers.split(",").map(s => s.trim()).filter(Boolean)
            : (Array.isArray(draft.oper_numbers) ? draft.oper_numbers : null),

          subscription_status: draft.subscription_status || "none",
          stripe_onboarding_status: draft.stripe_onboarding_status || "not_started",
          lifecycle_stage: draft.lifecycle_stage || "lead",
          onboarding_step: parseInt(draft.onboarding_step) || 0,
          onboarding_status: draft.onboarding_status || "not_started"
        })
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to save client");

      const updated = json.client;
      setSelectedClient(updated);
      setDraft(updated);
      setIsEditing(false);
    } catch (e) {
      console.error("CRM Admin: saved client failed", e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {
    window.location.href = "/dashboard";
    return null;
  }

  return (
    <div className="view-page clients">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h2 className="view-title">{t("clients.title")}</h2>
            <p className="view-subtitle">{t("clients.subtitle")}</p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.clients-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        <div className="card">
          <div className="panel-header">
            <div className="card-title">{t("clients.clientDetails")}</div>

            {draft && (
              <button
                className="btn-link"
                onClick={() => {
                  if (isEditing) {
                    setDraft(selectedClient);
                    setIsEditing(false);
                  } else {
                    setIsEditing(true);
                  }
                }}
              >
                {isEditing ? t("commonLabel.cancel") : t("commonLabel.edit")}
              </button>
            )}
          </div>

          {!draft ? (
            <p className="muted">{t("clients.noClientSelected")}</p>
          ) : (
            <>
              <div className="info-grid">
                <div className="info-row">
                  <label>{t("clients.businessName")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.business_name || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.business_name || ""}
                      onChange={e => setDraft(d => ({ ...d, business_name: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.ownerName")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.owner_name || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.owner_name || ""}
                      onChange={e => setDraft(d => ({ ...d, owner_name: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("commonLabel.phone")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.phone || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.phone || ""}
                      onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.email")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.email || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.email || ""}
                      onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.city")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.city || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.city || ""}
                      onChange={e => setDraft(d => ({ ...d, city: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.tierPlan")}</label>
                  {!isEditing ? (
                    <div className="info-box" style={{ textTransform: "capitalize" }}>
                      {draft.tier_plan || "none"}
                    </div>
                  ) : (
                    <select
                      className="input"
                      value={draft.tier_plan || "none"}
                      onChange={e => setDraft(d => ({ ...d, tier_plan: e.target.value }))}
                    >
                      <option value="none">{t("commonLabel.none", "None")}</option>
                      <option value="typical">{t("home.pricing.plan1.title", "Typical")}</option>
                      <option value="entrepreneur">{t("home.pricing.plan2.title", "Entrepreneur")}</option>
                      <option value="elite">{t("home.pricing.plan3.title", "Elite")}</option>
                    </select>
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.tierBilling")}</label>
                  {!isEditing ? (
                    <div className="info-box" style={{ textTransform: "capitalize" }}>
                      {draft.tier_billing === "yearly" ? t("home.pricing.yearly") : t("home.pricing.monthly")}
                    </div>
                  ) : (
                    <select
                      className="input"
                      value={draft.tier_billing || "monthly"}
                      onChange={e => setDraft(d => ({ ...d, tier_billing: e.target.value }))}
                    >
                      <option value="monthly">{t("home.pricing.monthly", "Monthly")}</option>
                      <option value="yearly">{t("home.pricing.yearly", "Yearly")}</option>
                    </select>
                  )}
                </div>

                <div className="info-row">
                  <label className="checkbox-label">{t("clients.contactOk")}</label>
                  {!isEditing ? (
                    <label className="checkbox-readonly">
                      <input type="checkbox" checked={!!draft.contact_ok} disabled />
                      <span className="checkbox-text">
                        {draft.contact_ok ? t("clients.allowContact") : t("clients.notAllowContact")}
                      </span>
                    </label>
                  ) : (
                    <label className="checkbox-editable">
                      <input
                        type="checkbox"
                        checked={!!draft.contact_ok}
                        onChange={e => setDraft(d => ({ ...d, contact_ok: e.target.checked }))}
                      />
                      <span className="checkbox-text">{t("clients.allowContact")}</span>
                    </label>
                  )}
                </div>

                <div className="info-row" style={{ gridColumn: "1 / -1" }}>
                  <label>{t("clients.clientRequest")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.client_request || "—"}</div>
                  ) : (
                    <textarea
                      className="input"
                      value={draft.client_request || ""}
                      onChange={e => setDraft(d => ({ ...d, client_request: e.target.value }))}
                      rows={3}
                    />
                  )}
                </div>

                <div className="info-row" style={{ gridColumn: "1 / -1" }}>
                  <label>{t("clients.nextAction")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.next_action || "—"}</div>
                  ) : (
                    <textarea
                      className="input"
                      value={draft.next_action || ""}
                      onChange={e => setDraft(d => ({ ...d, next_action: e.target.value }))}
                      rows={3}
                    />
                  )}
                </div>

                <div className="info-row" style={{ gridColumn: "1 / -1" }}>
                  <label>{t("clients.commentFeedback")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.comment_feedback || "—"}</div>
                  ) : (
                    <textarea
                      className="input"
                      value={draft.comment_feedback || ""}
                      onChange={e => setDraft(d => ({ ...d, comment_feedback: e.target.value }))}
                      rows={3}
                    />
                  )}
                </div>
              </div>

              <div
                className="full"
                style={{
                  marginTop: "20px",
                  marginBottom: "4px",
                  fontWeight: "bold",
                  gridColumn: "1 / -1",
                  borderBottom: "1px solid #d1d5db",
                  paddingBottom: "4px"
                }}
              >
                {t("clients.onboardingStatus")}
              </div>

              <div className="info-grid">
                <div className="info-row">
                  <label>{t("clients.lifecycleStage")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.lifecycle_stage || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.lifecycle_stage || ""}
                      onChange={e => setDraft(d => ({ ...d, lifecycle_stage: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.onboardingStep")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.onboarding_step ?? "—"}</div>
                  ) : (
                    <input
                      className="input"
                      type="number"
                      value={draft.onboarding_step ?? 0}
                      onChange={e => setDraft(d => ({ ...d, onboarding_step: parseInt(e.target.value) || 0 }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.onboardingStatusLabel")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.onboarding_status || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.onboarding_status || ""}
                      onChange={e => setDraft(d => ({ ...d, onboarding_status: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.subscriptionStatus")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.subscription_status || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.subscription_status || ""}
                      onChange={e => setDraft(d => ({ ...d, subscription_status: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.stripeOnboarding")}</label>
                  {!isEditing ? (
                    <div className="info-box">{draft.stripe_onboarding_status || "—"}</div>
                  ) : (
                    <input
                      className="input"
                      value={draft.stripe_onboarding_status || ""}
                      onChange={e => setDraft(d => ({ ...d, stripe_onboarding_status: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.activeEndsAt")}</label>
                  {!isEditing ? (
                    <div className="info-box">
                      {draft.active_ends_at
                        ? new Date(draft.active_ends_at).toLocaleString(currentLocale)
                        : "—"}
                    </div>
                  ) : (
                    <div className="datetime-split-row">
                      <input
                        className="input"
                        type="date"
                        value={
                          draft.active_ends_at
                            ? new Date(draft.active_ends_at).toISOString().slice(0, 10)
                            : ""
                        }
                        onChange={e => {
                          const newDate = e.target.value;
                          if (!newDate) return;
                          const current = draft.active_ends_at
                            ? new Date(draft.active_ends_at)
                            : new Date();
                          const timeStr = current.toTimeString().slice(0, 8);
                          setDraft(d => ({
                            ...d,
                            active_ends_at: new Date(`${newDate}T${timeStr}`).toISOString()
                          }));
                        }}
                      />
                      <input
                        className="input"
                        type="time"
                        value={
                          draft.active_ends_at
                            ? new Date(draft.active_ends_at).toTimeString().slice(0, 5)
                            : "00:00"
                        }
                        onChange={e => {
                          const newTime = e.target.value;
                          if (!newTime) return;
                          const current = draft.active_ends_at
                            ? new Date(draft.active_ends_at)
                            : new Date();
                          const dateStr = current.toISOString().slice(0, 10);
                          setDraft(d => ({
                            ...d,
                            active_ends_at: new Date(`${dateStr}T${newTime}:00`).toISOString()
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div
                className="full"
                style={{
                  marginTop: "20px",
                  marginBottom: "4px",
                  fontWeight: "bold",
                  gridColumn: "1 / -1",
                  borderBottom: "1px solid #d1d5db",
                  paddingBottom: "4px"
                }}
              >
                {t("clients.apiIntegration")}
              </div>

              <div className="info-grid">
                <div className="info-row">
                  <label>{t("clients.wabaPhoneId")}</label>
                  {!isEditing ? (
                    <div className="info-box" style={{ wordBreak: "break-all" }}>
                      {draft.waba_phone_number_id || "—"}
                    </div>
                  ) : (
                    <input
                      className="input"
                      value={draft.waba_phone_number_id || ""}
                      onChange={e => setDraft(d => ({ ...d, waba_phone_number_id: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row">
                  <label>{t("clients.d360ApiKey")}</label>
                  {!isEditing ? (
                    <div className="info-box" style={{ wordBreak: "break-all" }}>
                      {draft.d360_api_key || "—"}
                    </div>
                  ) : (
                    <input
                      className="input"
                      value={draft.d360_api_key || ""}
                      onChange={e => setDraft(d => ({ ...d, d360_api_key: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row" style={{ gridColumn: "1 / 3" }}>
                  <label>{t("clients.adminNumbers")}</label>
                  {!isEditing ? (
                    <div className="info-box">
                      {Array.isArray(draft.admin_numbers) && draft.admin_numbers.length > 0
                        ? draft.admin_numbers.join(", ")
                        : "—"}
                    </div>
                  ) : (
                    <input
                      className="input"
                      value={
                        Array.isArray(draft.admin_numbers)
                          ? draft.admin_numbers.join(", ")
                          : draft.admin_numbers || ""
                      }
                      onChange={e => setDraft(d => ({ ...d, admin_numbers: e.target.value }))}
                    />
                  )}
                </div>

                <div className="info-row" style={{ gridColumn: "1 / 3" }}>
                  <label>{t("clients.operatorNumbers")}</label>
                  {!isEditing ? (
                    <div className="info-box">
                      {Array.isArray(draft.oper_numbers) && draft.oper_numbers.length > 0
                        ? draft.oper_numbers.join(", ")
                        : "—"}
                    </div>
                  ) : (
                    <input
                      className="input"
                      value={
                        Array.isArray(draft.oper_numbers)
                          ? draft.oper_numbers.join(", ")
                          : draft.oper_numbers || ""
                      }
                      onChange={e => setDraft(d => ({ ...d, oper_numbers: e.target.value }))}
                    />
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="modal-actions" style={{ marginTop: 16 }}>
                  <button className="btn-primary" onClick={saveClient} disabled={saving}>
                    {saving ? t("clients.saving") : t("clients.saveChanges")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}